import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@/state/sessionStore';
import { PERSONA_CARDS, findPersonaCard } from '@/data/personas';
import { signInAsCard } from '@/domain/signIn';
import type { Persona } from '@/domain/types';
import { ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';

const PERSONA_LABEL: Record<Persona, string> = {
  georgina: 'Georgina, Ops Manager',
  messenger: 'Messenger',
  'studio-manager': 'Studio Manager',
  'bookings-agent': 'Bookings Agent',
  artist: 'Artist',
  apprentice: 'Apprentice',
  owner: 'Owner',
  finance: 'Finance / Accounts',
  'finance-manager': 'Finance Manager',
  confirmations: 'Confirmations',
  sweeper: 'Sweeper',
  floor: 'Floor Manager',
};

export default function PersonaSwitcher() {
  const navigate = useNavigate();
  const persona = useSessionStore((s) => s.persona);
  const setPersona = useSessionStore((s) => s.setPersona);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  if (!persona) return null;

  const currentCard = findPersonaCard(persona);

  const pick = (target: (typeof PERSONA_CARDS)[number]) => {
    setOpen(false);
    // Swap the login token too (B3): the server checks who sends each write. The
    // switch itself stays instant; writes queued meanwhile wait for the new token.
    void signInAsCard(target);
    setPersona(target.persona, {
      agentId: target.agentId,
      artistId: target.artistId,
      apprenticeId: target.apprenticeId,
      assignedStudios: target.assignedStudios,
      activeStudio: target.activeStudio,
    });
    navigate(target.destination);
  };

  return (
    <div ref={ref} className="relative" data-testid="persona-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="pl-3 ml-3 border-l border-gold-dim/30 flex flex-col text-right cursor-pointer group"
        data-testid="persona-switcher-trigger"
      >
        <span className="flex items-center justify-end gap-1.5 text-xs text-gold-bright font-inter uppercase tracking-wider group-hover:text-cream transition">
          {PERSONA_LABEL[persona]}
          <ChevronDown size={10} />
        </span>
        {currentCard ? (
          <span className="text-[10px] text-cream-soft uppercase tracking-widest">
            {currentCard.name}
          </span>
        ) : null}
      </button>

      {open ? (
        // Scrolls inside the viewport: with every seat listed the menu is taller than a laptop screen.
        <div
          className="absolute right-0 top-full mt-2 w-80 z-50 panel py-2 shadow-ink-soft max-h-[calc(100vh-5rem)] overflow-y-auto"
          data-testid="persona-switcher-menu"
        >
          <p className="font-inter text-[9px] uppercase tracking-widest text-gold-dim px-3 mb-1">
            Switch persona
          </p>
          {PERSONA_CARDS.filter((card) => !card.hidden || card.persona === persona).map((card) => {
            const selected = card.persona === persona;
            return (
              <button
                key={card.persona}
                type="button"
                onClick={() => pick(card)}
                className={clsx(
                  'w-full text-left px-3 py-2.5 transition flex items-start gap-3',
                  selected ? 'bg-gold/10' : 'hover:bg-ink-soft/50',
                )}
                data-testid={`persona-switch-${card.persona}`}
              >
                <span className="mt-1 shrink-0">
                  {selected ? (
                    <Check size={12} className="text-gold-bright" />
                  ) : (
                    <span className="block h-2 w-2 rounded-full bg-cream-soft/30" />
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="font-inter text-[9px] uppercase tracking-widest text-gold">
                    {card.role}
                  </span>
                  <span className="block font-inter text-sm text-cream mt-0.5">{card.name}</span>
                  <span className="block font-inter text-[11px] text-cream-soft mt-1 leading-snug">
                    Lands on {card.defaultHome}
                  </span>
                </span>
              </button>
            );
          })}
          <div className="border-t border-gold-dim/20 mt-2 pt-2 px-3 pb-1 flex items-center justify-between">
            <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
              Or {browser_meta()}⇧P
            </span>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/');
              }}
              className="font-inter text-[10px] text-gold-bright hover:text-cream uppercase tracking-widest"
            >
              Splash
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function browser_meta() {
  if (typeof navigator === 'undefined') return 'Cmd';
  return /Mac/i.test(navigator.platform) ? '⌘' : 'Ctrl';
}
